// frontend/src/visio/bench/AVBench.ts
// Core A/V sync measurement engine.
//
// Strategy (v2):
//   - Audio detection: AudioWorklet (Goertzel @ 1 kHz, sample-accurate, adaptive threshold).
//   - Video detection: requestVideoFrameCallback on the outgoing MediaStreamTrack (frame-accurate).
//   - Beep envelope: cosine ramp 2 ms attack/release (no click artefacts).
//   - Unified clock: performance.now(); audio timestamps converted via measured offset.
//   - Output: 3x3 SyncTable{dn, vbg} of Δ = V - A. VisioRoom uses max(0, -Δ) as video delay.

import { SelfieSegmentation, type Results as SelfieResults } from '@mediapipe/selfie_segmentation'
import { denoiseEngine } from '../../audio/DenoiseEngine'
import { createLogger } from '../../lib/logger'
import type {
  BenchCycle,
  BenchPass,
  BenchProfile,
  BenchProgress,
  BenchState,
  PassConfig,
} from './types'
import {
  BENCH_PASSES,
  CYCLES_PER_PASS,
  BURST_SEQUENCE,
  BURSTS_PER_SEQ,
  SEQUENCES_PER_PASS,
  BEEP_FREQ_HZ,
  FLASH_THRESHOLD,
  BEEP_THRESHOLD,
  LS_BENCH_KEY,
  buildSyncTable,
} from './types'

const slog = createLogger('bench')

type ProgressCallback = (progress: BenchProgress) => void

/** Result of one detector arm/detect cycle. */
interface DetectPromise {
  promise: Promise<number | null>
  cancel: () => void
}

export class AVBench {
  private aborted = false
  private progressCb: ProgressCallback | null = null
  private passes: BenchPass[] = []
  private state: BenchState = 'idle'
  private sourceStream: MediaStream | null = null
  private processedStream: MediaStream | null = null
  private currentPassCycles: BenchCycle[] = []

  /** Clock offset: audioTimeMs (from worklet) + offset = performance.now(). Measured per pass. */
  private audioClockOffsetMs: number = 0
  private lastAmbientNoise: number = 0
  private videoTrackFps: number = 0

  async run(onProgress: ProgressCallback): Promise<BenchProfile | null> {
    this.progressCb = onProgress
    this.aborted = false
    this.passes = []

    try {
      slog.action('bench-start', { totalPasses: BENCH_PASSES.length, cyclesPerPass: CYCLES_PER_PASS })

      for (let i = 0; i < BENCH_PASSES.length; i++) {
        if (this.aborted) break
        const config = BENCH_PASSES[i]
        this.emit('running', i, 0, config)
        const pass = await this.runPass(config, i)
        this.passes.push(pass)
        slog.action('pass-complete', {
          passIndex: i,
          label: config.label,
          avgLocalAudio: pass.avgLocalAudioLatencyMs,
          avgLocalVideo: pass.avgLocalVideoLatencyMs,
          diff21: pass.diffLocalMs,
        })
      }

      if (this.aborted) {
        slog.info('bench-abort', { completedPasses: this.passes.length })
        return null
      }

      const profile = this.buildProfile()
      try { localStorage.setItem(LS_BENCH_KEY, JSON.stringify(profile)) } catch { /* quota or private mode — non-fatal */ }
      this.emit('done', BENCH_PASSES.length - 1, CYCLES_PER_PASS - 1, null)
      slog.action('bench-done', { syncTable: profile.syncTable, passes: profile.passes.length })
      return profile
    } catch (err) {
      const msg = String(err instanceof Error ? err.message : err)
      slog.error('bench-error', { error: msg })
      this.emitError(msg)
      return null
    } finally {
      this.cleanup()
    }
  }

  abort() {
    this.aborted = true
    this.cleanup()
  }

  private async runPass(config: PassConfig, passIndex: number): Promise<BenchPass> {
    // ── Media capture ──
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: false, autoGainControl: true },
      video: { width: config.width, height: config.height, frameRate: config.fps },
    })

    // ── Audio graph ──
    // mic + beep  -> [denoise?] -> dest (this track is what we measure + what would be sent)
    // mic + beep  -> preDest              (always direct, for left "source" preview)
    const audioCtx = new AudioContext({ sampleRate: 48000 })
    const micSource = audioCtx.createMediaStreamSource(stream)
    const dest = audioCtx.createMediaStreamDestination()
    const preDest = audioCtx.createMediaStreamDestination()
    micSource.connect(preDest)

    // Beep: voiced signal (pulse train f0=250 Hz, Lorentzian-weighted harmonics
    // around 1 kHz with 1/k rolloff) so DeepFilterNet3 classifies it as voiced
    // speech and keeps it through. Offline tests against the actual DN ONNX
    // models show outGoertzel@1kHz ≈ 0.17 for bip1 (50 ms) and 0.07 for bip2
    // (10 ms, preconditioned by bip1 at -200 ms), both above the 0.05 detector
    // threshold. Pure tones, non-harmonic formants, AM, and chirps are all
    // gated out (< 0.005). The key is phase-coherent harmonics of a single f0.
    const F0 = 250
    const FORMANT = BEEP_FREQ_HZ // 1000
    const BW = 80
    const MAX_K = Math.floor(8000 / F0)
    const realCoef = new Float32Array(MAX_K + 1)
    const imagCoef = new Float32Array(MAX_K + 1)
    let coefTotal = 0
    for (let k = 1; k <= MAX_K; k++) {
      const f = k * F0
      const w = 1 / (1 + Math.pow((f - FORMANT) / BW, 2))
      const a = w / k
      if (a > 0.0025) { imagCoef[k] = a; coefTotal += a }
    }
    for (let k = 1; k <= MAX_K; k++) imagCoef[k] /= coefTotal  // sum = 1, peak ≤ 1
    const beepWave = audioCtx.createPeriodicWave(realCoef, imagCoef, { disableNormalization: true })
    const beepOsc = audioCtx.createOscillator()
    beepOsc.setPeriodicWave(beepWave)
    beepOsc.frequency.value = F0
    const beepGain = audioCtx.createGain()
    beepGain.gain.value = 0
    beepOsc.connect(beepGain)
    beepGain.connect(preDest)
    beepOsc.start()
    const beepOscs: OscillatorNode[] = [beepOsc]

    // Denoise stage
    let denoiseNode: AudioWorkletNode | null = null
    if (config.denoise !== 'off') {
      try {
        denoiseEngine.setPreset(config.denoise as 'sync' | 'async')
        denoiseNode = await denoiseEngine.createNode(audioCtx)
        denoiseEngine.setMode('on')
        micSource.connect(denoiseNode)
        beepGain.connect(denoiseNode)
        denoiseNode.connect(dest)
      } catch {
        micSource.connect(dest)
        beepGain.connect(dest)
      }
    } else {
      micSource.connect(dest)
      beepGain.connect(dest)
    }

    // ── Audio detector: AudioWorklet with Goertzel on the OUTGOING (post-denoise) stream ──
    const detCtx = new AudioContext({ sampleRate: 48000 })
    const detSource = detCtx.createMediaStreamSource(dest.stream)
    let detector: AudioWorkletNode | null = null
    let detSilentGain: GainNode | null = null
    try {
      await detCtx.audioWorklet.addModule('/av-bench-detector.worklet.js')
      detector = new AudioWorkletNode(detCtx, 'av-bench-detector')
      detSource.connect(detector)
      // Route detector -> muted gain -> destination to keep the audio graph alive.
      // Without this, Chrome may suspend/optimize away a node that has no downstream.
      detSilentGain = detCtx.createGain()
      detSilentGain.gain.value = 0
      detector.connect(detSilentGain)
      detSilentGain.connect(detCtx.destination)
      // Ensure context is actually running
      if (detCtx.state === 'suspended') await detCtx.resume()
      // Start the port explicitly (addEventListener requires it, unlike onmessage)
      detector.port.start()
      detector.port.postMessage({
        type: 'config',
        freq: BEEP_FREQ_HZ,
        windowSamples: 480, // 10 ms @ 48 kHz
        threshold: BEEP_THRESHOLD,
      })
    } catch (e) {
      slog.error('detector-init-failed', { error: String(e) })
    }

    // Calibrate clock offset: performance.now() - audioCtx.currentTime*1000
    // Worklet's currentTime is in detCtx frame; both contexts run on the same high-res clock.
    // We'll compute the offset right at arm time per-cycle (most accurate).
    this.audioClockOffsetMs = performance.now() - detCtx.currentTime * 1000

    // ── Video pipeline: two canvases (source preview + processed output) ──
    const video = document.createElement('video')
    video.srcObject = stream
    video.muted = true
    video.playsInline = true
    await video.play()

    const sourceCanvas = document.createElement('canvas')
    sourceCanvas.width = config.width
    sourceCanvas.height = config.height
    const sourceCtx = sourceCanvas.getContext('2d')!
    const sourceCanvasStream = sourceCanvas.captureStream(config.fps)

    const canvas = document.createElement('canvas')
    canvas.width = config.width
    canvas.height = config.height
    const ctx = canvas.getContext('2d')!
    const canvasStream = canvas.captureStream(config.fps)

    // ── VBG setup ──
    let vbgSeg: SelfieSegmentation | null = null
    let vbgMask: ImageBitmap | HTMLCanvasElement | HTMLImageElement | null = null
    let vbgBusy = false
    let vbgTmp: HTMLCanvasElement | null = null

    if (config.vbg !== 'none') {
      vbgSeg = new SelfieSegmentation({
        locateFile: (file: string) =>
          `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@0.1/${file}`,
      })
      // useCpuInference is a real option exposed by mediapipe but not in @types; widen
      // setOptions() type to Record<string, unknown> just for this call (no `any` leak).
      ;(vbgSeg.setOptions as (opts: Record<string, unknown>) => void)({
        modelSelection: 1,
        selfieMode: false,
        useCpuInference: config.vbg === 'cpu',
      })
      vbgSeg.onResults((results: SelfieResults) => {
        vbgMask = results.segmentationMask
        vbgBusy = false
      })
      await vbgSeg.initialize()
      vbgTmp = document.createElement('canvas')

      // Warm up
      for (let i = 0; i < 3; i++) {
        if (video.readyState >= video.HAVE_CURRENT_DATA) {
          await vbgSeg.send({ image: video })
        }
        await this.sleep(200)
      }
    }

    let flashActive = false
    let drawRaf = 0
    const drawLoop = () => {
      drawRaf = requestAnimationFrame(drawLoop)
      if (video.readyState >= video.HAVE_CURRENT_DATA) {
        sourceCtx.drawImage(video, 0, 0, config.width, config.height)

        if (vbgSeg && vbgMask) {
          vbgTmp!.width = config.width
          vbgTmp!.height = config.height
          const tCtx = vbgTmp!.getContext('2d')!
          ctx.save()
          ctx.filter = 'blur(10px)'
          ctx.drawImage(video, 0, 0, config.width, config.height)
          ctx.restore()
          tCtx.clearRect(0, 0, config.width, config.height)
          tCtx.drawImage(vbgMask, 0, 0, config.width, config.height)
          tCtx.globalCompositeOperation = 'source-in'
          tCtx.drawImage(video, 0, 0, config.width, config.height)
          ctx.drawImage(vbgTmp!, 0, 0, config.width, config.height)
          if (!vbgBusy) {
            vbgBusy = true
            vbgSeg.send({ image: video }).catch(() => { vbgBusy = false })
          }
        } else {
          ctx.drawImage(video, 0, 0, config.width, config.height)
        }

        if (flashActive) {
          sourceCtx.fillStyle = 'white'
          sourceCtx.fillRect(0, 0, config.width, config.height)
          ctx.fillStyle = 'white'
          ctx.fillRect(0, 0, config.width, config.height)
        }
      }
    }
    drawRaf = requestAnimationFrame(drawLoop)

    // Split-screen streams for UI
    const sourceMS = new MediaStream()
    for (const t of sourceCanvasStream.getVideoTracks()) sourceMS.addTrack(t)
    for (const t of preDest.stream.getAudioTracks()) sourceMS.addTrack(t)
    this.sourceStream = sourceMS

    const processedMS = new MediaStream()
    for (const t of canvasStream.getVideoTracks()) processedMS.addTrack(t)
    for (const t of dest.stream.getAudioTracks()) processedMS.addTrack(t)
    this.processedStream = processedMS

    // ── Video detector: consume the OUTGOING canvasStream via hidden <video> + rVFC ──
    const outVideo = document.createElement('video')
    outVideo.srcObject = canvasStream
    outVideo.muted = true
    outVideo.playsInline = true
    await outVideo.play()
    const probe = document.createElement('canvas')
    probe.width = 32
    probe.height = 32
    const probeCtx = probe.getContext('2d', { willReadFrequently: true })!

    // fps sampling (median of inter-frame intervals)
    const frameIntervals: number[] = []
    let lastFrameTime = 0

    this.currentPassCycles = []
    this.emit('running', passIndex, 0, config)

    // ── Per-cycle helpers ──
    // Arm the audio detector; resolve with audioTimeMs on detection (in performance.now epoch).
    const armAudio = (deadlineMs: number, cycleIdx: number, burstType: string): DetectPromise => {
      let resolve!: (v: number | null) => void
      const promise = new Promise<number | null>((res) => { resolve = res })
      const onMsg = (ev: MessageEvent) => {
        const d = ev.data
        if (d?.type === 'detect') {
          detector?.port.removeEventListener('message', onMsg)
          if (timeoutId !== undefined) clearTimeout(timeoutId)
          const perfMs = d.audioTimeMs + this.audioClockOffsetMs
          this.lastAmbientNoise = d.floor ?? this.lastAmbientNoise
          slog.info('audio-detect', { cycle: cycleIdx, burst: burstType, mag: Number(d.mag?.toFixed?.(4) ?? d.mag), floor: Number(d.floor?.toFixed?.(4) ?? d.floor) })
          resolve(perfMs)
        }
      }
      detector?.port.addEventListener('message', onMsg)
      // Re-calibrate clock offset right before arm
      this.audioClockOffsetMs = performance.now() - detCtx.currentTime * 1000
      // Arm without baseline — let the worklet rely on its own EMA noise floor
      detector?.port.postMessage({ type: 'arm' })
      const windowMs = Math.max(50, deadlineMs - performance.now())
      const timeoutId = window.setTimeout(() => {
        detector?.port.removeEventListener('message', onMsg)
        detector?.port.postMessage({ type: 'disarm' })
        slog.warn('audio-timeout', { cycle: cycleIdx, burst: burstType, windowMs: Math.round(windowMs) })
        resolve(null)
      }, windowMs)
      return {
        promise,
        cancel: () => {
          detector?.port.removeEventListener('message', onMsg)
          if (timeoutId !== undefined) clearTimeout(timeoutId)
          resolve(null)
        },
      }
    }

    // Arm the video detector via requestVideoFrameCallback until flash is seen.
    const armVideo = (deadlineMs: number, sentAt: number, cycleIdx: number, burstType: string): DetectPromise => {
      let resolve!: (v: number | null) => void
      let cancelled = false
      let framesSeen = 0
      let maxBright = 0
      const promise = new Promise<number | null>((res) => { resolve = res })
      type RVFCMeta = { mediaTime?: number; presentationTime?: number; expectedDisplayTime?: number }
      const anyVideo = outVideo as unknown as {
        requestVideoFrameCallback?: (cb: (now: number, meta: RVFCMeta) => void) => number
        cancelVideoFrameCallback?: (id: number) => void
      }
      let cbId = 0
      const checkFlash = (now: number): boolean => {
        try {
          probeCtx.drawImage(outVideo, 0, 0, 32, 32)
          const data = probeCtx.getImageData(12, 12, 8, 8).data
          let bright = 0
          for (let i = 0; i < data.length; i += 4) {
            const lum = (data[i] + data[i + 1] + data[i + 2]) / 3
            if (lum > FLASH_THRESHOLD) bright++
          }
          if (bright > maxBright) maxBright = bright
          if (bright > (8 * 8) / 2) {
            cancelled = true
            slog.info('video-detect', { cycle: cycleIdx, burst: burstType, bright, frames: framesSeen })
            resolve(now)
            return true
          }
        } catch { /* drawImage on detached track — non-fatal, frame skipped */ }
        return false
      }
      const onFrame = (now: number, meta: RVFCMeta) => {
        if (cancelled) return
        framesSeen++
        const mt = meta?.mediaTime ?? 0
        if (lastFrameTime > 0) frameIntervals.push(now - lastFrameTime)
        lastFrameTime = now
        if (checkFlash(now)) return
        if (performance.now() > deadlineMs) {
          cancelled = true
          slog.warn('video-timeout', { cycle: cycleIdx, burst: burstType, frames: framesSeen, maxBright, windowMs: Math.round(deadlineMs - sentAt) })
          resolve(null)
          return
        }
        void mt
        if (anyVideo.requestVideoFrameCallback) {
          cbId = anyVideo.requestVideoFrameCallback(onFrame)
        }
      }
      if (anyVideo.requestVideoFrameCallback) {
        cbId = anyVideo.requestVideoFrameCallback(onFrame)
      } else {
        const pollRaf = () => {
          if (cancelled) return
          framesSeen++
          if (checkFlash(performance.now())) return
          if (performance.now() > deadlineMs) {
            cancelled = true
            slog.warn('video-timeout', { cycle: cycleIdx, burst: burstType, frames: framesSeen, maxBright, windowMs: Math.round(deadlineMs - sentAt), mode: 'raf-fallback' })
            resolve(null)
            return
          }
          requestAnimationFrame(pollRaf)
        }
        requestAnimationFrame(pollRaf)
      }
      return {
        promise,
        cancel: () => {
          cancelled = true
          if (cbId && anyVideo.cancelVideoFrameCallback) anyVideo.cancelVideoFrameCallback(cbId)
          resolve(null)
        },
      }
    }

    // Envelope: cosine ramp on gain 0 -> 0.8 over 2 ms, hold, 0.8 -> 0 over 2 ms.
    const injectBeep = (durationMs: number) => {
      const t0 = audioCtx.currentTime
      const ramp = 0.002
      beepGain.gain.cancelScheduledValues(t0)
      beepGain.gain.setValueAtTime(0, t0)
      beepGain.gain.linearRampToValueAtTime(0.8, t0 + ramp)
      beepGain.gain.setValueAtTime(0.8, t0 + Math.max(ramp, durationMs / 1000))
      beepGain.gain.linearRampToValueAtTime(0, t0 + Math.max(ramp, durationMs / 1000) + ramp)
    }

    // Minimum detection window accounts for:
    //  - denoise buffering (0 / 100 / 500 ms)
    //  - video pipeline: rAF (16 ms) + captureStream frame interval (1/fps ≈ 45 ms) + <video> buffering
    //  - safety margin
    const minDetectionMs = config.denoise === 'async' ? 1200 : config.denoise === 'sync' ? 600 : 300

    // Outgoing canvas track — used for requestFrame() to force immediate frame emission on flash
    const canvasVideoTrack = canvasStream.getVideoTracks()[0] as MediaStreamTrack & { requestFrame?: () => void }
    const sourceVideoTrack = sourceCanvasStream.getVideoTracks()[0] as MediaStreamTrack & { requestFrame?: () => void }
    const forceFrame = () => {
      try { canvasVideoTrack?.requestFrame?.() } catch { /* track ended — non-fatal */ }
      try { sourceVideoTrack?.requestFrame?.() } catch { /* track ended — non-fatal */ }
    }

    // Warm-up: 2 dummy beeps+flashes before real measurement to stabilize pipelines
    for (let w = 0; w < 2; w++) {
      if (this.aborted) break
      injectBeep(50)
      flashActive = true
      ctx.fillStyle = 'white'; ctx.fillRect(0, 0, config.width, config.height)
      sourceCtx.fillStyle = 'white'; sourceCtx.fillRect(0, 0, config.width, config.height)
      forceFrame()
      await this.sleep(50)
      flashActive = false
      await this.sleep(Math.max(minDetectionMs, 600))
    }

    // ── Run cycles ──
    const cycles: BenchCycle[] = []
    for (let seq = 0; seq < SEQUENCES_PER_PASS; seq++) {
      if (this.aborted) break
      let seqCursor = performance.now()

      for (let b = 0; b < BURST_SEQUENCE.length; b++) {
        if (this.aborted) break
        const burst = BURST_SEQUENCE[b]
        const flatIndex = seq * BURSTS_PER_SEQ + b
        this.emit('running', passIndex, flatIndex, config)

        if (b > 0) {
          const gap = BURST_SEQUENCE[b].offsetMs - BURST_SEQUENCE[b - 1].offsetMs
          const settle = Math.max(50, gap)
          const wait = seqCursor + settle - performance.now()
          if (wait > 0) await this.sleep(wait)
        }

        const nextGap = b < BURST_SEQUENCE.length - 1
          ? BURST_SEQUENCE[b + 1].offsetMs - burst.offsetMs - burst.durationMs
          : 3000
        const detectionMs = Math.max(minDetectionMs, nextGap)

        const cycle = await this.runCycle(
          flatIndex,
          burst.offsetMs,
          burst.durationMs,
          burst.type,
          detectionMs,
          injectBeep,
          armAudio,
          armVideo,
          () => {
            flashActive = true
            ctx.fillStyle = 'white'; ctx.fillRect(0, 0, config.width, config.height)
            sourceCtx.fillStyle = 'white'; sourceCtx.fillRect(0, 0, config.width, config.height)
            forceFrame()
          },
          () => { flashActive = false },
        )
        cycles.push(cycle)
        this.currentPassCycles.push(cycle)
        this.emit('running', passIndex, flatIndex, config)
        seqCursor = performance.now()
      }

      if (seq < SEQUENCES_PER_PASS - 1) await this.sleep(800)
    }

    // Track fps computation
    if (frameIntervals.length > 3) {
      const sorted = [...frameIntervals].sort((a, b) => a - b)
      const median = sorted[Math.floor(sorted.length / 2)]
      if (median > 0) this.videoTrackFps = Math.round(1000 / median)
    }

    // ── Cleanup pass resources ──
    cancelAnimationFrame(drawRaf)
    this.sourceStream = null
    this.processedStream = null
    for (const o of beepOscs) { try { o.stop() } catch { /* already stopped */ } }
    try { detector?.disconnect() } catch { /* already disconnected */ }
    try { await detCtx.close() } catch { /* already closed */ }
    try { await audioCtx.close() } catch { /* already closed */ }
    for (const t of stream.getTracks()) t.stop()
    try { outVideo.srcObject = null } catch { /* element detached */ }
    video.srcObject = null
    if (vbgSeg) { vbgSeg.close().catch(() => {}); vbgSeg = null }

    // ── Averages ──
    const avg = (arr: (number | null)[]) => {
      const valid = arr.filter((v): v is number => v !== null && Number.isFinite(v))
      if (valid.length === 0) return null
      // Use median for robustness
      const sorted = [...valid].sort((a, b) => a - b)
      const mid = Math.floor(sorted.length / 2)
      const m = sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
      return Math.round(m)
    }

    const aLA = avg(cycles.map(c => c.localAudioLatencyMs))
    const aLV = avg(cycles.map(c => c.localVideoLatencyMs))

    const audioOk = cycles.filter(c => c.localAudioLatencyMs !== null).length
    const videoOk = cycles.filter(c => c.localVideoLatencyMs !== null).length
    slog.info('pass-done', {
      label: config.label,
      denoise: config.denoise,
      vbg: config.vbg,
      total: cycles.length,
      audioOk,
      videoOk,
      avgAudioMs: aLA,
      avgVideoMs: aLV,
      diffMs: aLV !== null && aLA !== null ? aLV - aLA : null,
      trackFps: this.videoTrackFps,
    })

    return {
      config,
      cycles,
      avgLocalAudioLatencyMs: aLA,
      avgLocalVideoLatencyMs: aLV,
      diffLocalMs: aLV !== null && aLA !== null ? aLV - aLA : null,
    }
  }

  private async runCycle(
    index: number,
    burstOffsetMs: number,
    burstDurationMs: number,
    burstType: 'bip1' | 'bip2',
    detectionWindowMs: number,
    injectBeep: (durMs: number) => void,
    armAudio: (deadline: number, cycleIdx: number, burstType: string) => DetectPromise,
    armVideo: (deadline: number, sentAt: number, cycleIdx: number, burstType: string) => DetectPromise,
    flashOn: () => void,
    flashOff: () => void,
  ): Promise<BenchCycle> {
    const cycle: BenchCycle = {
      index,
      burstOffsetMs,
      burstType,
      sentAt: 0,
      localAudioDetectedAt: null,
      localVideoDetectedAt: null,
      localAudioLatencyMs: null,
      localVideoLatencyMs: null,
    }

    const sentAt = performance.now()
    cycle.sentAt = sentAt
    const deadline = sentAt + detectionWindowMs

    // Arm detectors FIRST, then inject
    const ap = armAudio(deadline, index, burstType)
    const vp = armVideo(deadline, sentAt, index, burstType)

    // Inject beep + flash simultaneously
    injectBeep(burstDurationMs)
    flashOn()
    setTimeout(() => { flashOff() }, burstDurationMs)

    const [aMs, vMs] = await Promise.all([ap.promise, vp.promise])
    if (this.aborted) { ap.cancel(); vp.cancel() }

    if (aMs !== null) {
      cycle.localAudioDetectedAt = aMs
      cycle.localAudioLatencyMs = Math.round(aMs - sentAt)
    }
    if (vMs !== null) {
      cycle.localVideoDetectedAt = vMs
      cycle.localVideoLatencyMs = Math.round(vMs - sentAt)
    }

    slog.info('cycle-done', {
      cycle: index,
      burst: burstType,
      audioMs: cycle.localAudioLatencyMs,
      videoMs: cycle.localVideoLatencyMs,
      windowMs: detectionWindowMs,
    })

    return cycle
  }

  private cleanup(): void {
    this.sourceStream = null
    this.processedStream = null
  }

  // ── Profile building ──

  private buildProfile(): BenchProfile {
    const syncTable = buildSyncTable(this.passes)
    return {
      version: 2,
      timestamp: new Date().toISOString(),
      passes: this.passes,
      syncTable,
      ambientNoise: this.lastAmbientNoise,
      audioClockOffsetMs: this.audioClockOffsetMs,
      videoTrackFps: this.videoTrackFps,
      userAgent: navigator.userAgent.slice(0, 120),
    }
  }

  // ── Helpers ──

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  private emit(state: BenchState, passIndex: number, cycleIndex: number, config: PassConfig | null) {
    this.state = state
    void this.state
    this.progressCb?.({
      state,
      passIndex,
      totalPasses: BENCH_PASSES.length,
      cycleIndex,
      cyclesPerPass: CYCLES_PER_PASS,
      currentConfig: config,
      passes: [...this.passes],
      sourceStream: this.sourceStream,
      processedStream: this.processedStream,
      currentPassCycles: [...this.currentPassCycles],
    })
  }

  private emitError(error: string) {
    this.state = 'error'
    this.progressCb?.({
      state: 'error',
      passIndex: 0,
      totalPasses: BENCH_PASSES.length,
      cycleIndex: 0,
      cyclesPerPass: CYCLES_PER_PASS,
      currentConfig: null,
      passes: [...this.passes],
      sourceStream: null,
      processedStream: null,
      currentPassCycles: [],
      error,
    })
  }
}

/** Read the saved bench profile from localStorage. */
export function loadBenchProfile(): BenchProfile | null {
  try {
    const raw = localStorage.getItem(LS_BENCH_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    // Drop v1 profiles (no syncTable) — user will re-run.
    if (!parsed || typeof parsed !== 'object' || !parsed.syncTable) return null
    return parsed as BenchProfile
  } catch {
    return null
  }
}
